import api from './api';
import { Product } from '@/types';

export interface Supplier {
  id: number;
  tenant_id: number;
  name: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  address?: string;
  notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  products_count?: number;
}

export interface SupplierCreate {
  name: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  address?: string;
  notes?: string;
}

export interface SupplierUpdate {
  name?: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  address?: string;
  notes?: string;
  is_active?: boolean;
}

export interface SupplierFilters {
  search?: string;
  only_active?: boolean;
  skip?: number;
  limit?: number;
}

export interface BulkPriceUpdateRequest {
  supplier_id?: number;
  category_id?: number;
  update_type: 'cost' | 'sale_price' | 'list_price' | 'all';
  percentage: number;
  recalculate_from_cost: boolean;
  reason?: string;
}

export interface BulkPriceUpdateResponse {
  updated_count: number;
  products_updated: number[];
}

export const suppliersService = {
  async getSuppliers(filters?: SupplierFilters): Promise<Supplier[]> {
    const response = await api.get<Supplier[]>('/suppliers', { params: filters });
    return response.data;
  },

  async getSupplier(id: number): Promise<Supplier> {
    const response = await api.get<Supplier>(`/suppliers/${id}`);
    return response.data;
  },

  async createSupplier(data: SupplierCreate): Promise<Supplier> {
    const response = await api.post<Supplier>('/suppliers', data);
    return response.data;
  },

  async updateSupplier(id: number, data: SupplierUpdate): Promise<Supplier> {
    const response = await api.put<Supplier>(`/suppliers/${id}`, data);
    return response.data;
  },

  async deleteSupplier(id: number): Promise<void> {
    await api.delete(`/suppliers/${id}`);
  },

  async getSupplierProducts(id: number, onlyActive: boolean = true): Promise<Product[]> {
    const response = await api.get<Product[]>(`/suppliers/${id}/products`, {
      params: { only_active: onlyActive },
    });
    return response.data;
  },

  async bulkUpdatePrices(
    supplierId: number,
    data: Omit<BulkPriceUpdateRequest, 'supplier_id'>
  ): Promise<BulkPriceUpdateResponse> {
    const response = await api.post<BulkPriceUpdateResponse>(
      `/suppliers/${supplierId}/bulk-price-update`,
      data
    );
    return response.data;
  },
};
